/**
 * Color Utility Functions
 *
 * Helper functions for color manipulation, contrast calculation, etc.
 */

/**
 * Convert hex color to RGB object
 * @param {string} hex - Hex color code (with or without #)
 * @returns {{r: number, g: number, b: number}} RGB values
 */
export const hexToRgb = (hex) => {
  const cleanHex = hex.replace('#', '');
  const bigint = parseInt(cleanHex, 16);
  return {
    r: (bigint >> 16) & 255,
    g: (bigint >> 8) & 255,
    b: bigint & 255,
  };
};

/**
 * Convert RGB to hex color
 * @param {number} r - Red (0-255)
 * @param {number} g - Green (0-255)
 * @param {number} b - Blue (0-255)
 * @returns {string} Hex color code with #
 */
export const rgbToHex = (r, g, b) => {
  return '#' + [r, g, b].map((x) => {
    const hex = x.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  }).join('');
};

/**
 * Calculate relative luminance of a color
 * Based on WCAG 2.1 formula
 * @param {string} hex - Hex color code
 * @returns {number} Luminance value (0-1)
 */
export const getLuminance = (hex) => {
  const { r, g, b } = hexToRgb(hex);

  const toLinear = (value) => {
    const normalized = value / 255;
    return normalized <= 0.03928
      ? normalized / 12.92
      : Math.pow((normalized + 0.055) / 1.055, 2.4);
  };

  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
};

/**
 * Calculate contrast ratio between two colors
 * @param {string} hex1 - First hex color
 * @param {string} hex2 - Second hex color
 * @returns {number} Contrast ratio (1-21)
 */
export const getContrastRatio = (hex1, hex2) => {
  const lum1 = getLuminance(hex1);
  const lum2 = getLuminance(hex2);
  const lighter = Math.max(lum1, lum2);
  const darker = Math.min(lum1, lum2);
  return (lighter + 0.05) / (darker + 0.05);
};

/**
 * Get contrasting text color (black or white) for a background
 * @param {string} bgHex - Background hex color
 * @returns {string} '#ffffff' or '#000000'
 */
export const getContrastingTextColor = (bgHex) => {
  const luminance = getLuminance(bgHex);
  // Use 0.179 as threshold (WCAG recommended)
  return luminance > 0.179 ? '#000000' : '#ffffff';
};

/**
 * Get complementary color for text that ensures good contrast
 * Similar to attendee app's getComplementaryColor
 * @param {string} primaryColor - Primary color hex
 * @returns {string} Contrasting color for text
 */
export const getComplementaryColor = (primaryColor) => {
  if (!primaryColor || typeof primaryColor !== 'string') {
    return '#ffffff';
  }

  try {
    const { r, g, b } = hexToRgb(primaryColor);

    // Calculate relative luminance
    const toLinear = (value) => {
      const normalized = value / 255;
      return normalized <= 0.03928
        ? normalized / 12.92
        : Math.pow((normalized + 0.055) / 1.055, 2.4);
    };

    const luminance = 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);

    // Return white for dark colors, dark for light colors
    return luminance > 0.5 ? '#1a1a1a' : '#ffffff';
  } catch (error) {
    console.warn('Error calculating complementary color:', error);
    return '#ffffff';
  }
};

/**
 * Lighten a color by a percentage
 * @param {string} hex - Hex color code
 * @param {number} percent - Percentage to lighten (0-100)
 * @returns {string} Lightened hex color
 */
export const lightenColor = (hex, percent) => {
  const { r, g, b } = hexToRgb(hex);
  const amount = Math.round(2.55 * percent);
  return rgbToHex(
    Math.min(255, r + amount),
    Math.min(255, g + amount),
    Math.min(255, b + amount)
  );
};

/**
 * Darken a color by a percentage
 * @param {string} hex - Hex color code
 * @param {number} percent - Percentage to darken (0-100)
 * @returns {string} Darkened hex color
 */
export const darkenColor = (hex, percent) => {
  const { r, g, b } = hexToRgb(hex);
  const amount = Math.round(2.55 * percent);
  return rgbToHex(
    Math.max(0, r - amount),
    Math.max(0, g - amount),
    Math.max(0, b - amount)
  );
};

/**
 * Add alpha (opacity) to a hex color
 * @param {string} hex - Hex color code
 * @param {number} alpha - Alpha value (0-1)
 * @returns {string} RGBA color string
 */
export const hexToRgba = (hex, alpha) => {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/**
 * Check if a string is a valid hex color
 * @param {string} hex - String to validate
 * @returns {boolean} True if valid hex color
 */
export const isValidHex = (hex) => {
  if (!hex || typeof hex !== 'string') return false;
  const cleanHex = hex.replace('#', '');
  return /^[0-9A-Fa-f]{6}$/.test(cleanHex) || /^[0-9A-Fa-f]{3}$/.test(cleanHex);
};

/**
 * Apply primary color to CSS variables
 * @param {string} primaryColor - Primary color hex
 */
export const applyPrimaryColor = (primaryColor) => {
  if (!isValidHex(primaryColor)) {
    console.warn('Invalid primary color:', primaryColor);
    return;
  }

  const root = document.documentElement;
  const complementary = getComplementaryColor(primaryColor);

  root.style.setProperty('--color-primary', primaryColor);
  root.style.setProperty('--color-primary-hover', darkenColor(primaryColor, 10));
  root.style.setProperty('--color-primary-active', darkenColor(primaryColor, 20));
  root.style.setProperty('--color-primary-light', hexToRgba(primaryColor, 0.1));
  root.style.setProperty('--color-comp-primary', complementary);

  // Legacy variable support
  root.style.setProperty('--primaryColor', primaryColor);
};

/**
 * Apply secondary color to CSS variables
 * @param {string} secondaryColor - Secondary color hex
 */
export const applySecondaryColor = (secondaryColor) => {
  if (!isValidHex(secondaryColor)) {
    console.warn('Invalid secondary color:', secondaryColor);
    return;
  }

  const root = document.documentElement;
  const complementary = getComplementaryColor(secondaryColor);

  root.style.setProperty('--color-secondary', secondaryColor);
  root.style.setProperty('--color-secondary-hover', darkenColor(secondaryColor, 10));
  root.style.setProperty('--color-secondary-active', darkenColor(secondaryColor, 20));
  root.style.setProperty('--color-secondary-light', hexToRgba(secondaryColor, 0.1));
  root.style.setProperty('--color-secondary-contrast', complementary);

  // Legacy variable support
  root.style.setProperty('--secondaryColor', secondaryColor);
};

/**
 * Apply font family to CSS variables
 * @param {string} fontFamily - Font family name
 */
export const applyFontFamily = (fontFamily) => {
  if (!fontFamily || typeof fontFamily !== 'string') return;

  const root = document.documentElement;
  const fontStack = `'${fontFamily}', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`;

  root.style.setProperty('--font-family-primary', fontStack);
  root.style.setProperty('--font-family', fontStack);
};
